/* System-level screens & wrappers: ErrorBoundary, NotFoundScreen,
 * OfflineScreen, OfflineBanner.
 */
import React, { useState, useEffect } from 'react';
import { C } from '../tokens.js';

export const StatusScreen = ({ emoji, title, body, primaryLabel, onPrimary, secondaryLabel, onSecondary, tone = 'neutral' }) => {
  const accent = tone === 'error' ? C.error : tone === 'warning' ? C.warning : C.primary;
  return (
    <div className="fade-in" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, background: C.bg, textAlign: 'center' }}>
      <div style={{ fontSize: 72, marginBottom: 20 }}>{emoji}</div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 10 }}>{title}</h2>
      <p style={{ fontSize: 14, color: C.textMed, lineHeight: 1.55, marginBottom: 28, maxWidth: 300 }}>{body}</p>
      {primaryLabel && <button onClick={onPrimary} style={{ background: accent, color: C.white, border: 'none', borderRadius: 14, padding: '13px 28px', fontSize: 15, fontWeight: 700, cursor: 'pointer', minWidth: 200 }}>{primaryLabel}</button>}
      {secondaryLabel && <button onClick={onSecondary} style={{ marginTop: 12, background: 'none', border: 'none', color: accent, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{secondaryLabel}</button>}
    </div>
  );
};

export const NotFoundScreen = ({ onHome }) => (
  <StatusScreen
    emoji="🧭"
    title="Page not found"
    body="The screen you tried to open doesn't exist or was removed. Head back home and try again."
    primaryLabel="Back to home"
    onPrimary={onHome}
  />
);

export const OfflineScreen = ({ onRetry }) => (
  <StatusScreen
    emoji="📡"
    title="You're offline"
    body="We couldn't reach our servers. Check your connection and try again — your cart and bookings are safe."
    primaryLabel="Retry"
    onPrimary={onRetry}
    tone="warning"
  />
);

export const OfflineBanner = () => {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  if (online) return null;
  return (
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 50, background: C.warning, color: C.white, padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 12, fontWeight: 700, boxShadow: '0 2px 8px rgba(0,0,0,.12)' }}>
      <span>⚠️</span>
      <span>You're offline — changes will sync when you're back online</span>
    </div>
  );
};

/* Persistent banner shown above any screen while the customer is in
   supplier-lock mode (arrived via an invite URL, hasn't paid yet). */
export const SupplierLockBanner = ({ branchName, onInfo }) => (
  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 40, background: 'linear-gradient(135deg,#0cc0df,#2dd4ed)', color: C.white, padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12, fontWeight: 700, boxShadow: '0 2px 8px rgba(12,192,223,.30)' }}>
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
      <span style={{ fontSize: 14 }}>📬</span>
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Invited by {branchName}</span>
    </span>
    <button onClick={onInfo} style={{ background: 'rgba(255,255,255,.18)', border: 'none', color: C.white, width: 22, height: 22, borderRadius: 11, fontSize: 11, fontWeight: 800, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>ℹ</button>
  </div>
);

/* Shown when a customer opens an invite URL that has already been
   consumed (paid) or cancelled. Single CTA to enter the normal app. */
export const InviteUsedScreen = ({ branchName, status, onContinue }) => {
  const message = status === 'cancelled'
    ? `This invite from ${branchName || 'the supplier'} was cancelled.`
    : status === 'expired'
      ? `This invite from ${branchName || 'the supplier'} has expired.`
      : `This invite from ${branchName || 'the supplier'} has already been used.`;
  return (
    <StatusScreen
      emoji="🔗"
      title="Invite no longer active"
      body={message + ' You can still browse Rekraft normally.'}
      primaryLabel="Continue to Rekraft"
      onPrimary={onContinue}
    />
  );
};

/* PWA install prompt.
 *
 * Captures the Chromium `beforeinstallprompt` event globally on first load,
 * stashes it on window, and replays it when the customer taps Install.
 * Triggered after sign-in (see App.jsx). Suppressed if already running in
 * standalone mode or previously dismissed. iOS Safari has no programmatic
 * prompt — we show the manual Share → Add to Home Screen instructions. */

const INSTALL_DISMISS_KEY = 'rekraft.installDismissed';

const isStandalone = () =>
  (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
  || (typeof navigator !== 'undefined' && navigator.standalone === true);

const isIosSafari = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const webkit = /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return iOS && webkit;
};

if (typeof window !== 'undefined' && !window.__rekraftInstallHooked) {
  window.__rekraftInstallHooked = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    window.__rekraftDeferredInstall = e;
  });
  window.addEventListener('appinstalled', () => {
    window.__rekraftDeferredInstall = null;
    try { localStorage.setItem(INSTALL_DISMISS_KEY, 'installed'); } catch { /* ignore */ }
  });
}

export const canShowInstallPrompt = () => {
  if (isStandalone()) return false;
  try { if (localStorage.getItem(INSTALL_DISMISS_KEY)) return false; } catch { /* ignore */ }
  return !!window.__rekraftDeferredInstall || isIosSafari();
};

export const InstallPwaModal = ({ onClose }) => {
  const [busy, setBusy] = useState(false);
  const ios = isIosSafari();

  const dismiss = (persist = true) => {
    if (persist) {
      try { localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    }
    onClose();
  };

  const install = async () => {
    const deferred = window.__rekraftDeferredInstall;
    if (!deferred) { dismiss(); return; }
    setBusy(true);
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch { /* user cancelled */ }
    window.__rekraftDeferredInstall = null;
    setBusy(false);
    dismiss();
  };

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 120, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={() => dismiss()}>
      <div className="slide-up" style={{ background: C.white, borderRadius: '24px 24px 0 0', width: '100%', padding: '20px 22px 28px' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: C.border }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: `linear-gradient(135deg,${C.primary},${C.accent})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white, fontWeight: 900, fontSize: 24, flexShrink: 0 }}>R</div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Install Rekraft</p>
            <p style={{ fontSize: 12, color: C.textMed }}>Faster launch, full-screen, works offline.</p>
          </div>
        </div>
        {ios ? (
          <div style={{ background: C.bg, borderRadius: 14, padding: '14px 16px', marginBottom: 16 }}>
            <p style={{ fontSize: 13, color: C.text, lineHeight: 1.55 }}>
              In Safari, tap the <b>Share</b> button <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: 'middle' }}><path d="M12 3v12M8 7l4-4 4 4M5 13v6a2 2 0 002 2h10a2 2 0 002-2v-6" stroke={C.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span> at the bottom of the screen, then choose <b>Add to Home Screen</b>.
            </p>
          </div>
        ) : (
          <p style={{ fontSize: 13, color: C.textMed, lineHeight: 1.55, marginBottom: 18 }}>
            Add Rekraft to your home screen for a one-tap, app-like experience — no browser bar, instant launch.
          </p>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => dismiss()} style={{ flex: 1, padding: '13px 16px', borderRadius: 12, border: `1.5px solid ${C.border}`, background: C.white, color: C.text, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            Not now
          </button>
          {!ios && (
            <button onClick={install} disabled={busy} style={{ flex: 1, padding: '13px 16px', borderRadius: 12, border: 'none', background: C.primary, color: C.white, fontSize: 14, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}>
              {busy ? 'Installing…' : 'Install'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export class ErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error('[Rekraft] uncaught render error', err, info);
  }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="phone">
        <div className="scr">
          <StatusScreen
            emoji="💥"
            title="Something went wrong"
            body="We hit an unexpected error. Reloading usually fixes it."
            primaryLabel="Reload app"
            onPrimary={() => window.location.reload()}
            secondaryLabel={this.props.onReset ? 'Back to home' : null}
            onSecondary={this.props.onReset ? () => { this.setState({ err: null }); this.props.onReset(); } : null}
            tone="error"
          />
        </div>
      </div>
    );
  }
}
